import { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { lazily } from 'react-lazily';

import { PASSWORD_PATTERN } from '@login/lib/constants';

import styles from './password-fields.module.scss';

const { Insert } = lazily(() => import('core/generic/elements/form'));

export type PasswordLabels = {
	password: string;
	passwordConfirm: string;
	complexityError: string;
	nonEnglishError: string;
	matchesUsernameError: string;
	mustMatchError: string;
};

type ResolveFieldError = (formMessage?: string, apiKey?: string) => string;

type PasswordFieldsProps = {
	labels: PasswordLabels;
	/** Field whose value the password must not equal, typically the email. */
	usernameField?: string;
	resolveError?: ResolveFieldError;
};

const isAsciiOnly = (value: string) =>
	Array.from(value).every((char) => char.charCodeAt(0) <= 127);

/**
 * Password and confirmation, with the complexity rules the account forms share.
 */
const PasswordFieldsInner = ({
	labels,
	usernameField = 'email',
	resolveError,
}: PasswordFieldsProps) => {
	const {
		register,
		getValues,
		formState: { errors },
	} = useFormContext();

	const errorFor = (formMessage?: string, apiKey?: string) =>
		resolveError?.(formMessage, apiKey) ?? formMessage ?? '';

	return (
		<div className={styles.passwordSection}>
			<Insert
				{...register('password', {
					validate: (value: string) => {
						if (!isAsciiOnly(value)) return labels.nonEnglishError;
						if (!PASSWORD_PATTERN.test(value))
							return labels.complexityError;
						if (value === getValues(usernameField))
							return labels.matchesUsernameError;
						return true;
					},
				})}
				defaultValue={''}
				id="password"
				label={labels.password}
				required={true}
				type="password"
				error={errorFor(errors.password?.message as string, 'Password')}
				errorIcon="AlertCircleFilled"
			/>
			<Insert
				{...register('passwordConfirm', {
					required: labels.mustMatchError,
					validate: (value: string) =>
						value === getValues('password') ||
						labels.mustMatchError,
				})}
				defaultValue={''}
				id="passwordConfirm"
				label={labels.passwordConfirm}
				required={true}
				type="password"
				error={errorFor(
					errors.passwordConfirm?.message as string,
					'PasswordConfirm'
				)}
				errorIcon="AlertCircleFilled"
			/>
		</div>
	);
};

export const PasswordFields = memo(PasswordFieldsInner);

export default PasswordFields;
